import React, {
    ReactElement,
    useState,
} from 'react';
import './dealer-selection-dropdown.scss';
import cn from 'classnames';
import {DealerItem} from './EmbeddedDealerCodeChange';
import Select, {
    ActionMeta,
    createFilter,
    components,
    OptionProps,
    StylesConfig,
    SingleValue
} from 'react-select';
import {Shimmer} from '@mo/shimmer';

export interface DealerSelectionDropdownProps {
    onSelection: (newValue: string) => void;
    role: 'card' | 'menu-item';
    dealers: DealerItem[];
    labelText?: string;
    loading?: boolean;
    inError?: boolean;
    clearable?: boolean;
    minCharacters?: number;
    classname?: string;
}

export interface Option {
    value: string;
    label: string;
    text: string;
    status: 'Open' | 'Closed' | 'Closing Down';
}

export function StatusFlag({status}: { status: 'Open' | 'Closed' | 'Closing Down' }) {
    if (!status || status === 'Open') {
        return <></>
    }
    return <span className='option-tag'>{status} dealership</span>
}

function Option(props: OptionProps<Option, false>) {
    return (
        <components.Option {...props}>
            <div role='link' className='dealerSelectionDropdown__option'>
                <span className='option-text'>{props.data.text}</span> <StatusFlag status={props.data.status}/>
            </div>
        </components.Option>
    );
}

function DealerSelectionDropdown({
                                     clearable = false,
                                     inError = false,
                                     onSelection,
                                     role = 'card',
                                     dealers,
                                     labelText,
                                     minCharacters = 2,
                                     loading,
                                     classname
                                 }: DealerSelectionDropdownProps): ReactElement {
    const [dealerCode, setDealerCode] = useState('');
    const [showOptions, setShowOptions] = useState(false);

    function onChange(selectedOption: SingleValue<Option>, {action}: ActionMeta<Option>) {
        if (action === 'select-option') {
            const code = (selectedOption as Option).value;
            setDealerCode(code);
            onSelection(code);
        } else if (action === 'clear') {
            setDealerCode('');
            onSelection('');
        }
    }

    function handleInputChange(newValue: string) {
        if (newValue.length >= minCharacters) {
            setShowOptions(true);
        } else {
            setShowOptions(false);
        }
    }

    const options = dealers.map(({code, status, address, name}): Option => {
        const statusText = !status || status === 'Open' ? '' : `${status} dealership`;
        return {
            value: code,
            label: `${name} - ${code} - ${address} - ${statusText}`,
            text: `${name} - ${code} - ${address}`,
            status
        };
    });

    const styleOverrides: StylesConfig<Option, boolean> = {
        control: (styles, {isFocused}) => {
            return {
                ...styles,
                '&:hover': {
                    borderColor: inError ? '#db3624' : '#999',
                },
                borderWidth: inError ? 3 : 2,
                borderColor: inError ? '#db3624' : '#999',
                boxShadow: isFocused ? '0 0 0 3px #f58923' : 'inset 0 1px 3px 0 rgba(0,0,0,.2)',
            };
        },
        dropdownIndicator: () => ({display: 'none'}),
        indicatorSeparator: () => ({display: 'none'}),
        input: (styles) => ({...styles, paddingBottom: 0, paddingTop: 0}),
        menuList: (styles) => ({...styles, paddingBottom: 0, paddingTop: 0}),
        noOptionsMessage: (styles) => ({...styles, textAlign: 'left'}),
        option: (styles) => {
            return {
                ...styles,
                ':active': {
                    backgroundColor: '#c2d6ee',
                },
                cursor: 'pointer',
            };
        },
    };

    const searchPrompt = labelText || 'Type to search by code, postcode, or name';
    return (
        <Shimmer loading={loading}>
            <div className={cn('dealerSelectionDropdown', {dealerSelectionDropdown__card: role === 'card'}, classname)}>
                <label>
                    <p id='dealerSelectionDropdown__searchPrompt' className='dealerSelectionDropdown__searchPrompt'>
                        {searchPrompt}
                    </p>
                    <div className={'autocompleteBox'} data-shimmer-value=''>
                        <Select
                            components={{Option}}
                            aria-label={
                                'Start typing to search for a dealer, by code, postcode, or name. Type 2 or more characters to search. When autocomplete results are available use the up and down arrow keys to review and select. Touch device users, use swipe gestures'
                            }
                            aria-labelledby={'dealerSelectionDropdown__searchPrompt'}
                            className={'autocompleteInput'}
                            options={showOptions ? options : []}
                            defaultInputValue={dealerCode}
                            isClearable={clearable}
                            onChange={(selectedOption: SingleValue<Option>, action: ActionMeta<Option>) =>
                                onChange(selectedOption, action)
                            }
                            classNamePrefix={'dealer-selection-dropdown-prefix'}
                            filterOption={createFilter({ignoreAccents: false})}
                            onInputChange={(newValue: string) => handleInputChange(newValue)}
                            noOptionsMessage={() => (showOptions ? `No results found` : null)}
                            theme={(defaultTheme) => {
                                return {
                                    ...defaultTheme,
                                    borderRadius: 6,
                                    spacing: {
                                        baseUnit: 7,
                                        controlHeight: 12,
                                        menuGutter: 3,
                                    },
                                };
                            }}
                            styles={styleOverrides}
                            placeholder={<div aria-hidden={true}>Type to search...</div>}
                        />
                    </div>
                </label>
            </div>
        </Shimmer>
    );
}

export default DealerSelectionDropdown;
